"use client";

import React, { useEffect } from "react";
import { useEntityInstanceModalStack } from "@/stores/entity-instance-modal-stack-store";
import type { ItemsToSet } from "@/services/entity-instance.service";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { CreateOrUpdateEntityInstanceContent } from "./create-or-update-entity-instance-content";
import { usePermissionForEntityId } from "@/hooks/schema/use-my-permissions";
import { useEntityByName } from "@/hooks/schema/use-entity-by-name";
import { AnimatePresence, motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { KEYBOARD_SHORTCUTS, matchesShortcut } from "@/lib/keyboard-shortcuts";

/**
 * EntityInstanceModalHost
 *
 * Renders multiple modals (one for each depth level) to show the stack visually.
 * Position calculation happens once here and is passed down to the wrapper.
 * Framer Motion handles all animations.
 */
export const EntityInstanceModalHost: React.FC = () => {
  const { stack, pop } = useEntityInstanceModalStack();

  const handleClose = React.useCallback(() => {
    pop();
  }, [pop]);

  const isOpen = stack.length > 0;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // A nested dialog, menu or popover that closed on Escape marks it handled.
      if (e.defaultPrevented) return;
      if (isOpen && matchesShortcut(e, KEYBOARD_SHORTCUTS["record-modal-close"])) {
        pop();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, pop]);

  return (
    <DialogPrimitive.Root
      open={isOpen}
      modal={false}
      onOpenChange={(open) => {
        if (!open) {
          pop();
        }
      }}
    >
      <DialogPrimitive.Portal>
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
              className="fixed inset-0 z-40 modal-backdrop"
              onClick={handleClose}
            />
          )}
        </AnimatePresence>
        <AnimatePresence mode="sync">
          {stack.map((view, index) => {
            const isTopLevel = index === stack.length - 1;
            // Calculate position from the right based on current stack position
            // Top modal: 8px from right, second: 150px, third: 300px, etc.
            const distanceFromTop = stack.length - 1 - index;
            const rightPosition = distanceFromTop === 0 ? 8 : 150 * distanceFromTop;

            return (
              <EntityInstanceModalContent
                key={view.key}
                entityName={view.entityName}
                entityInstanceId={view.entityInstanceId}
                entityFromListCache={view.entityFromListCache}
                fieldsToHide={view.fieldsToHide}
                initialValues={view.initialValues}
                initialRelations={view.initialRelations}
                onOpenChanges={handleClose}
                onSave={async (data) => {
                  if (view.onSave) {
                    return await view.onSave(data);
                  }
                }}
                isTopLevel={isTopLevel}
                rightPosition={rightPosition}
                isBottomLevel={index === 0}
              />
            );
          })}
        </AnimatePresence>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

/**
 * Wrapper that handles permissions fetching for the entity
 */
const EntityInstanceModalContent: React.FC<{
  entityName: string;
  entityInstanceId?: string;
  entityFromListCache?: object;
  fieldsToHide?: Set<string>;
  initialValues?: Record<string, unknown>;
  initialRelations?: ItemsToSet;
  onOpenChanges: () => void;
  onSave: ({
    dontTriggerFlows,
    data,
  }: {
    dontTriggerFlows: boolean;
    data: { [field: string]: unknown };
  }) => Promise<unknown>;
  isTopLevel: boolean;
  rightPosition: number;
  isBottomLevel: boolean;
}> = ({
  entityName,
  entityInstanceId,
  entityFromListCache,
  fieldsToHide,
  initialValues,
  initialRelations,
  onOpenChanges,
  onSave,
  isTopLevel,
  rightPosition,
  isBottomLevel,
}) => {
  const { entity } = useEntityByName(entityName);
  const permissions = usePermissionForEntityId(entity?.id ?? "");

  return (
    <motion.div
      initial={{ x: "100%", opacity: 0, right: `${rightPosition}px` }}
      animate={{ x: 0, opacity: 1, right: `${rightPosition}px` }}
      exit={{ x: "100%", opacity: 0, right: `${rightPosition}px` }}
      transition={{
        x: { duration: 0.5, ease: [0.4, 0, 0.2, 1] },
        right: { duration: 0.5, ease: [0.4, 0, 0.2, 1] },
        opacity: { duration: 0.2 },
      }}
      className={cn(
        "fixed inset-y-0 bottom-2 top-2 z-50",
        "flex w-3/4 flex-col overflow-hidden rounded-lg border-l bg-white shadow-lg",
        "sm:max-w-[min(900px,70svw)]",
      )}
      style={{
        pointerEvents: isTopLevel ? "auto" : "none", // Disable clicks on non-top modals
      }}
      onKeyDown={(e) => {
        // Stop propagation for the "open entity modal" shortcut (and Enter) to prevent opening duplicate modals
        const native = e.nativeEvent as unknown as KeyboardEvent;
        if (
          matchesShortcut(native, KEYBOARD_SHORTCUTS["table-open-record"]) ||
          native.key === "Enter"
        ) {
          e.stopPropagation();
        }
      }}
    >
      <CreateOrUpdateEntityInstanceContent
        entityName={entityName}
        entityInstanceId={entityInstanceId}
        entityFromListCache={entityFromListCache}
        initialValues={initialValues}
        initialRelations={initialRelations}
        isOpen={true}
        onOpenChanges={(open) => {
          if (!open && isTopLevel) {
            onOpenChanges();
          }
        }}
        onSave={onSave}
        fieldsToHide={fieldsToHide ?? new Set()}
        permissions={permissions}
        isBottomLevel={isBottomLevel}
      />
    </motion.div>
  );
};
